import { useState } from "react";
import styles from "./PublicationSelection.module.scss";
import useWhitelabelAvailability from "../../hooks/useWhitelabelAvailability";
import useSalesforce from "../../hooks/useSalesforce.js";
import SelectedIcon from "../../assets/icons/Selected.svg";
import UnSelectedIcon from "../../assets/icons/Unselected.svg";
import ActiveIcon from "../../assets/icons/Active.svg";
import GuidelineSvgPath from "../../assets/icons/Guideline.svg";
import DRTCLogoSvgPath from "../../assets/icons/DRTCLogo.svg";
import ObesiDSvgPath from "../../assets/icons/ObesiD.svg";
import AIDHLogoSvgPath from "../../assets/icons/AIDHLogo.svg";
import { storeConnectUrl } from "../../authConfig";

const PublicationSelection = ({
  onProceedToPurchase,
  activeSubs = [],
  initialSelectedApps = [],
  initialPurchaseOption = "singleUser",
  initialStep = 1,
}) => {
  const [selectedApps, setSelectedApps] = useState(initialSelectedApps);
  const [purchaseOption, setPurchaseOption] = useState(initialPurchaseOption);
  const [step, setStep] = useState(initialStep === 2 ? 2 : 1);
  const { isWhitelabelEnabled } = useWhitelabelAvailability();
  const { isSalesforceEnabled } = useSalesforce();
  const publications = [
    {
      id: "tg",
      subId: "151",
      name: "Therapeutic Guidelines",
      icon: "Guideline",
      iconPath: GuidelineSvgPath,
    },
    {
      id: "obesid",
      subId: "2151",
      name: "ObesiD",
      icon: "ObesiD",
      iconPath: ObesiDSvgPath,
    },
    {
      id: "drtc",
      subId: "3151",
      name: "Don't Rush to Crush",
      icon: "DRTCLogo",
      iconPath: DRTCLogoSvgPath,
    },
    {
      id: "aidh",
      subId: "4151",
      name: "AIDH",
      icon: "AIDHLogo",
      iconPath: AIDHLogoSvgPath,
    },
  ];

  const isAlreadyOwned = (subId) => {
    return activeSubs.some((active) => String(active.tg_subscriptionid) === String(subId));
  };

  const availablePubs = publications
    .filter((pub) => isWhitelabelEnabled(pub.id))
    .filter((pub) => !isAlreadyOwned(pub.subId));

  const handleToggleApp = (appId, subId) => {
    if (isAlreadyOwned(subId)) return;

    setSelectedApps((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId],
    );
  };

  const handleProceed = () => {
    if (selectedApps.length > 0) {
      onProceedToPurchase({
        selectedApps,
        availableApps: availablePubs.map((pub) => pub.id),
        purchaseOption,
      });
    }
  };
  const handleEnquiryClick = (e) => {
    e.preventDefault();

    // If no apps are selected, we can either default to all available apps
    // or alert the user. Usually, it's best to send whatever is selected,
    // or all available if none are checked.
    const appsToEnquire = selectedApps.length > 0 ? selectedApps : availablePubs.map((p) => p.id);

    if (appsToEnquire.length === 0) {
      alert("Please select a publication or contact sales@tg.org.au directly.");
      return;
    }

    onProceedToPurchase({
      selectedApps: appsToEnquire,
      availableApps: availablePubs.map((pub) => pub.id),
      purchaseOption: "institution",
    });
  };

  if (activeSubs.length > 0 && !isSalesforceEnabled) {
    return (
      <div className={styles.simplifiedWrapper}>
        <span className={styles.pageTitle}>Select a subscription</span>

        <div className={styles.headerGroup}>
          <div className={styles.subTitle}>Single User Subscriptions</div>
          <p className={styles.description}>
            Select one or multiple subscriptions to our products. For institutional subscriptions
            please complete an{" "}
            <span href="#" onClick={handleEnquiryClick} className={styles.enquiryLink}>
              enquiry form
            </span>
            .
          </p>
        </div>

        <div className={styles.selectionList}>
          {availablePubs.map((pub) => (
            <div
              key={pub.id}
              className={`${styles.selectionRow} ${selectedApps.includes(pub.id) ? styles.activeRow : ""}`}
              onClick={() => handleToggleApp(pub.id, pub.subId)}
            >
              <div className={styles.leftGroup}>
                <div className={styles.radioCircle}>
                  {selectedApps.includes(pub.id) && <div className={styles.radioInner} />}
                </div>
                <img alt={pub.id} src={pub.iconPath} className={styles.selectedIcon} />
              </div>

              <div className={styles.rowContent}>
                <span className={styles.rowLabel}>
                  <strong>12 Month Subscription</strong> | Individual User
                </span>
              </div>
            </div>
          ))}

          {availablePubs.length === 0 && (
            <div className={styles.emptyState}>
              <strong>
                You currently have active subscriptions to all available individual products.
              </strong>
            </div>
          )}
        </div>

        <button
          className={styles.fullWidthBtn}
          onClick={handleProceed}
          disabled={selectedApps.length === 0}
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className={styles.purchaseWrapper}>
      {step === 1 ? (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Select Subscription</h2>
            <div className={styles.planGrid}>
              <div
                className={`${styles.planCard} ${purchaseOption === "singleUser" ? styles.activePlan : ""}`}
                onClick={() => setPurchaseOption("singleUser")}
              >
                <div className={styles.IconContainer}>
                  <div className={styles.radio}>
                    {purchaseOption === "singleUser" && <div className={styles.radioInner} />}
                  </div>
                  <div>
                    <strong>Single user</strong>
                  </div>
                </div>

                <div className={styles.planInfo}>
                  <span>Individual user account and access</span>
                </div>
              </div>

              <div
                className={`${styles.planCard} ${purchaseOption === "institution" ? styles.activePlan : ""}`}
                onClick={() => setPurchaseOption("institution")}
              >
                <div className={styles.IconContainer}>
                  <div className={styles.radio}>
                    {purchaseOption === "institution" && <div className={styles.radioInner} />}
                  </div>
                  <div>
                    <strong>Institution</strong>
                  </div>
                </div>
                <div className={styles.planInfo}>
                  <span>All organisations with more than 1 user.</span>
                </div>
              </div>
            </div>
          </section>

          <div className={styles.actionRow}>
            <button
              className={styles.mainActionBtn}
              onClick={() => {
                if (purchaseOption === "institution") {
                  onProceedToPurchase({
                    selectedApps,
                    availableApps: availablePubs.map((pub) => pub.id),
                    purchaseOption,
                  });
                } else {
                  if (isSalesforceEnabled) {
                    window.location.replace(`${storeConnectUrl}subscriptions`);
                  } else {
                    setStep(2);
                  }
                }
              }}
            >
              Continue
            </button>
          </div>
        </>
      ) : (
        <>
          {!isSalesforceEnabled && (
            <>
              <section className={styles.section}>
                <h2 className={`${styles.sectionTitle} ${styles.sectionTitleSm}`}>
                  Select Publications
                </h2>
                <div className={styles.cardRow}>
                  {availablePubs.map((pub) => {
                    const owned = isAlreadyOwned(pub.subId);
                    return (
                      <div
                        key={pub.id}
                        className={`
                      ${styles.pubCard}
                      ${selectedApps.includes(pub.id) ? styles.activeCard : ""}
                      ${owned ? styles.disabledCard : ""}
                    `}
                        onClick={() => handleToggleApp(pub.id, pub.subId)}
                      >
                        <div className={styles.selectionOptions}>
                          {selectedApps.includes(pub.id) ? (
                            <img src={SelectedIcon} alt="selected" />
                          ) : (
                            <img src={UnSelectedIcon} alt="unSelected" />
                          )}
                          {owned && (
                            <img src={ActiveIcon} alt="active" className={styles.ownedIcon} />
                          )}
                        </div>
                        <div className={styles.iconContainer}>
                          <img alt={pub.id} src={pub.iconPath} className={styles.appLogo} />
                          <div>
                            <span className={styles.subscriptionInfo}>12 Month Subscription </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className={styles.actionRow}>
                <button
                  className={styles.mainActionBtn}
                  onClick={handleProceed}
                  disabled={selectedApps.length === 0}
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PublicationSelection;
